import type { ColorPreset, ThemeColors, ThemeMode } from "../api/types";

/* The two built-in palettes, mirroring `.surface-hub` and `.surface-hub.mode-dark` in
   tokens.css. The admin's saved colors are what the console actually paints with, so
   switching mode swaps an untouched palette for the other mode's instead of leaving a
   light palette on a dark console. */
export const LIGHT_COLORS: ThemeColors = {
  bg: "#F5F7FA", bgAlt: "#ECF0F6", surface: "#FFFFFF", line: "#DFE5EE",
  textHi: "#181B31", textMid: "#181B31", textLo: "#181B31",
  accent: "#1F4585", accentHi: "#16305E",
};

export const DARK_COLORS: ThemeColors = {
  bg: "#0F1420", bgAlt: "#161D2C", surface: "#1B2333", line: "#2A3448",
  textHi: "#F4F6FB", textMid: "#C7D0E0", textLo: "#8892A8",
  accent: "#5C8AD6", accentHi: "#7FA3E0",
};

export function defaultsFor(mode: ThemeMode): ThemeColors {
  return mode === "dark" ? DARK_COLORS : LIGHT_COLORS;
}

export function sameColors(a: ThemeColors, b: ThemeColors): boolean {
  return (Object.keys(a) as (keyof ThemeColors)[]).every((k) => a[k].toLowerCase() === b[k].toLowerCase());
}

export type NamedPreset = Exclude<ColorPreset, "custom">;

const c = (
  bg: string, bgAlt: string, surface: string, line: string,
  textHi: string, textMid: string, textLo: string, accent: string, accentHi: string,
): ThemeColors => ({ bg, bgAlt, surface, line, textHi, textMid, textLo, accent, accentHi });

/* Ready-made palettes, each with a light and a dark variant. "naidapa" follows the Naidapa
   ERPNext theme (teal #0F626A, ink #082F32, page #F5FBFB). Every palette clears 4.5:1 for
   heading/body text and the accent on the page (see palettes.test.ts). */
export const PRESETS: Record<NamedPreset, { light: ThemeColors; dark: ThemeColors }> = {
  hub: { light: LIGHT_COLORS, dark: DARK_COLORS },
  naidapa: {
    light: c("#F5FBFB", "#E7EFF0", "#FFFFFF", "#EBEDF0", "#082F32", "#082F32", "#4B6467", "#0F626A", "#0A4A50"),
    dark: c("#0B1F21", "#102A2D", "#143236", "#24494D", "#EAF6F6", "#B9D2D4", "#86A7AA", "#4FB6BF", "#7ACDD4"),
  },
  forest: {
    light: c("#F3F7F4", "#E4EDE7", "#FFFFFF", "#D8E3DB", "#14281E", "#14281E", "#4A6355", "#1F7A4D", "#165C39"),
    dark: c("#0E1A14", "#14251C", "#182D22", "#274434", "#ECF7F0", "#BBD3C4", "#86A493", "#4CC38A", "#7AD6A8"),
  },
  sunset: {
    light: c("#FBF6F1", "#F3E8DD", "#FFFFFF", "#EADBCB", "#2A1A10", "#2A1A10", "#6B5444", "#B45309", "#8A3F07"),
    dark: c("#1A1210", "#241A16", "#2B201B", "#43322A", "#FBF1E8", "#DCC7B6", "#A78E7C", "#F0994A", "#F5B577"),
  },
  violet: {
    light: c("#F7F5FC", "#ECE8F7", "#FFFFFF", "#E0DAF0", "#1E1633", "#1E1633", "#5B5078", "#6D28D9", "#5019A6"),
    dark: c("#14101F", "#1B162B", "#221B36", "#382E55", "#F3EFFC", "#CFC6E6", "#9C90BD", "#A78BFA", "#C0AAFB"),
  },
};

export const PRESET_IDS = Object.keys(PRESETS) as NamedPreset[];

/** The nine colors a preset paints in the given mode. "custom" has no palette of its own. */
export function resolvePreset(id: ColorPreset, mode: ThemeMode): ThemeColors {
  return id === "custom" ? defaultsFor(mode) : PRESETS[id][mode];
}
